<script setup lang="ts">
import CarouselImage1 from "./../../assets/image/productTechnology/kart/carousel-1.jpg";
import CarouselImage2 from "./../../assets/image/productTechnology/kart/carousel-2.jpg";
import CarouselImage3 from "./../../assets/image/productTechnology/kart/carousel-3.jpg";
import CarouselImage4 from "./../../assets/image/productTechnology/kart/carousel-4.jpg";
import CarouselImage5 from "./../../assets/image/productTechnology/kart/carousel-5.jpg";
import CarouselImage6 from "./../../assets/image/productTechnology/kart/carousel-6.jpg";
import { refDebounced, TransitionPresets, useTransition, useScroll } from "@vueuse/core";
import { ref, computed, watch } from "vue";

const emit = defineEmits(["goTo"]);
const gallery = ref(null);
const cursor = computed({
    get() {
        return gallery.value?.cursor === undefined ? 0 : gallery.value?.cursor;
    },
    set(value) {
        gallery.value?.goTo(value);
    },
});
const cursorDebounced = refDebounced(cursor, 200);
const cursorTransition = useTransition(cursor, {
    duration: 1000,
    delay: 0,
    easing: TransitionPresets.easeInOutBack,
});
const cursorCeilTransition = computed(() => Math.ceil(cursorTransition.value));

const timelineElement = ref(null);
const timelineElements = ref<HTMLElement[]>([]);
const scroll = useScroll(timelineElement, {
    behavior: "smooth",
});

watch(
    cursor,
    (value) => {
        const selectedElement = timelineElements.value[value];
        if (selectedElement === undefined) return;
        scroll.x.value = selectedElement.offsetLeft;
    },
    {
        immediate: true,
    },
);

function goTo(path) {
    emit("goTo", path);
}
</script>

<template>
    <VGallery
        ref="gallery"
        class="timeline-gallery"
        :hide-dots="true"
        :disable-loop="true">
        <VGalleryPage :index="0" :class="{ show: cursorDebounced === 0 }">
            <img :src="CarouselImage1" class="background" alt="identity" />
            <VFlex flex-direction="column" class="content" align-items="start">
                <h3
                    class="has-text-primary is-size-4 is-uppercase has-text-left is-weight-900">
                    <Translate>.identity.title</Translate>
                </h3>
                <p class="has-text-white has-text-left is-weight-500 desc-text is-size-6">
                    <Translate>.identity.description</Translate>
                </p>
                <VButton color="secondary" size="big" @click="goTo('identity')">
                    <Translate>.identity.button</Translate>
                </VButton>
            </VFlex>
        </VGalleryPage>
        <VGalleryPage :index="1" :class="{ show: cursorDebounced === 1 }">
            <img :src="CarouselImage2" class="background" alt="performance" />
            <VFlex flex-direction="column" class="content" align-items="start">
                <h3
                    class="has-text-primary is-size-4 is-uppercase has-text-left is-weight-900">
                    <Translate>.performance.title</Translate>
                </h3>
                <p class="has-text-white has-text-left is-weight-500 desc-text is-size-6">
                    <Translate>.performance.description</Translate>
                </p>
                <VButton color="secondary" @click="goTo('performance')">
                    <Translate>.performance.button</Translate>
                </VButton>
            </VFlex>
        </VGalleryPage>
        <VGalleryPage :index="2" :class="{ show: cursorDebounced === 2 }">
            <img :src="CarouselImage3" class="background" alt="conception" />
            <VFlex flex-direction="column" class="content" align-items="start">
                <h3
                    class="has-text-primary is-size-4 is-uppercase has-text-left is-weight-900">
                    <Translate>.conception.title</Translate>
                </h3>
                <p class="has-text-white has-text-left is-weight-500 desc-text is-size-6">
                    <Translate>.conception.description</Translate>
                </p>
                <VButton color="secondary" @click="goTo('conception')">
                    <Translate>.conception.button</Translate>
                </VButton>
            </VFlex>
        </VGalleryPage>
        <VGalleryPage :index="3" :class="{ show: cursorDebounced === 3 }">
            <img :src="CarouselImage4" class="background" alt="security" />
            <VFlex flex-direction="column" class="content" align-items="start">
                <h3
                    class="has-text-primary is-size-4 is-uppercase has-text-left is-weight-900">
                    <Translate>.security.title</Translate>
                </h3>
                <p class="has-text-white has-text-left is-weight-500 desc-text is-size-6">
                    <Translate>.security.description</Translate>
                </p>
                <VButton color="secondary" @click="goTo('security')">
                    <Translate>.security.button</Translate>
                </VButton>
            </VFlex>
        </VGalleryPage>
        <VGalleryPage :index="4" :class="{ show: cursorDebounced === 4 }">
            <img :src="CarouselImage5" class="background" alt="sustainability" />
            <VFlex flex-direction="column" class="content" align-items="start">
                <h3
                    class="has-text-primary is-size-4 is-uppercase has-text-left is-weight-900">
                    <Translate>.sustainability.title</Translate>
                </h3>
                <p class="has-text-white has-text-left is-weight-500 desc-text is-size-6">
                    <Translate>.sustainability.description</Translate>
                </p>
                <VButton color="secondary" @click="goTo('sustainability')">
                    <Translate>.sustainability.button</Translate>
                </VButton>
            </VFlex>
        </VGalleryPage>
        <VGalleryPage :index="5" :class="{ show: cursorDebounced === 5 }">
            <img :src="CarouselImage6" class="background" alt="profitability" />
            <VFlex flex-direction="column" class="content" align-items="start">
                <h3
                    class="has-text-primary is-size-4 is-uppercase has-text-left is-weight-900">
                    <Translate>.profitability.title</Translate>
                </h3>
                <p class="has-text-white has-text-left is-weight-500 desc-text is-size-6">
                    <Translate>.profitability.description</Translate>
                </p>
                <VButton color="secondary" size="big" @click="goTo('profitability')">
                    <Translate>.profitability.button</Translate>
                </VButton>
            </VFlex>
        </VGalleryPage>
        <div ref="timelineElement" class="progression">
            <span class="top"></span>
            <a
                :ref="(ref) => (timelineElements[0] = ref)"
                role="button"
                tabindex="0"
                :class="{
                    previous: cursorCeilTransition >= 0,
                    current: cursor === 0,
                }"
                @keydown="cursor = 0"
                @click="cursor = 0">
                <span class="top"></span>
                <span class="left"></span>
                <span class="progression-title">
                    <Translate>.identity.title</Translate>
                </span>
            </a>
            <a
                :ref="(ref) => (timelineElements[1] = ref)"
                :class="{
                    previous: cursorCeilTransition >= 1,
                    current: cursor === 1,
                }"
                role="button"
                tabindex="0"
                @keydown="cursor = 1"
                @click="cursor = 1">
                <span class="top"></span>
                <span class="left"></span>
                <span class="progression-title">
                    <Translate>.performance.title</Translate>
                </span>
            </a>
            <a
                :ref="(ref) => (timelineElements[2] = ref)"
                :class="{
                    previous: cursorCeilTransition >= 2,
                    current: cursor === 2,
                }"
                role="button"
                tabindex="0"
                @keydown="cursor = 2"
                @click="cursor = 2">
                <span class="top"></span>
                <span class="left"></span>
                <span class="progression-title">
                    <Translate>.conception.title</Translate>
                </span>
            </a>
            <a
                :ref="(ref) => (timelineElements[3] = ref)"
                :class="{
                    previous: cursorCeilTransition >= 3,
                    current: cursor === 3,
                }"
                role="button"
                tabindex="0"
                @keydown="cursor = 3"
                @click="cursor = 3">
                <span class="top"></span>
                <span class="left"></span>
                <span class="progression-title">
                    <Translate>.security.title</Translate>
                </span>
            </a>
            <a
                :ref="(ref) => (timelineElements[4] = ref)"
                :class="{
                    previous: cursorCeilTransition >= 4,
                    current: cursor === 4,
                }"
                role="button"
                tabindex="0"
                @keydown="cursor = 4"
                @click="cursor = 4">
                <span class="top"></span>
                <span class="left"></span>
                <span class="progression-title">
                    <Translate>.sustainability.title</Translate>
                </span>
            </a>
            <a
                :ref="(ref) => (timelineElements[5] = ref as HTMLElement)"
                :class="{
                    previous: cursorCeilTransition >= 5,
                    current: cursor === 5,
                }"
                role="button"
                tabindex="0"
                @keydown="cursor = 5"
                @click="cursor = 5">
                <span class="top"></span>
                <span class="left"></span>
                <span class="progression-title">
                    <Translate>.profitability.title</Translate>
                </span>
            </a>
        </div>
    </VGallery>
</template>
<style lang="scss">
h3 {
    font-family: "Bebas Neue";
}

.timeline-gallery {
    height: 700px;

    .vgallery-page {
        &::after {
            background-image: radial-gradient(70% 50%, transparent 0, #000 100%);
            content: "";
            min-height: 100%;
            min-width: 100%;
            position: absolute;
            top: 0;
            left: 0;
        }

        .background {
            position: absolute;
            width: 100%;
            height: 100%;
            object-fit: cover;
            opacity: 0;
            transition: opacity 0.5s ease-in-out;
        }

        .content {
            position: absolute;
            z-index: 1;
            padding: 20px;
            left: 10%;
            top: 10%;
            max-width: 450px;
            color: white;
            text-align: center;
            transition: all 0.5s ease-in-out;
            transform: translateY(50px);
            opacity: 0.7;
        }

        &.show {
            .background {
                opacity: 1;
            }

            .content {
                transform: translateY(0);
                opacity: 1;
            }
        }

        .desc-text {
            @media screen and (max-width: 520px) {
                font-size: 12px;
                max-width: 280px;
            }
        }
    }

    .progression {
        position: absolute;
        bottom: 100px;
        left: 0;
        right: 0;
        display: flex;
        padding: 0;
        color: white;
        font-size: 0.8rem;
        font-weight: bold;
        text-transform: uppercase;
        transition: all 0.5s ease-in-out;
        z-index: 2;
        height: 82px;
        overflow-x: auto;
        -ms-overflow-style: none; /* Internet Explorer 10+ */
        scrollbar-width: none; /* Firefox */
        box-sizing: border-box;

        > .top {
            position: absolute;
            content: "";
            top: 0;
            left: 0;
            height: 5px;
            width: 100%;
            background: white;
        }

        a {
            cursor: pointer;
            color: white;
            transition: color 0.5s ease-in-out;
            padding: 0;
            margin: 0;
            width: 200px;
            position: relative;

            &::after {
                position: absolute;
                content: "";
                top: 0;
                left: 0;
                height: 5px;
                width: 100%;
                background: white;
                z-index: 0;
            }

            &:last-of-type {
                margin-right: 100px;
            }

            > .top {
                position: absolute;
                top: 0;
                height: 5px;
                width: 0;
                transition: width 0.5s ease-in-out;
                background: var(--primary);
                z-index: 1;
            }

            &::before {
                content: "";
                display: block;
                width: 3px;
                height: 20px;
                background-color: white;
                margin: 0 0 0 195px;
                transition: all 0.5s ease-in-out;
            }

            > .progression-title {
                padding: 5px 10px;
                margin: 0;
                position: absolute;
                font-size: 14px;
                right: 2px;
            }

            &.current {
                > .progression-title {
                    background: var(--primary);
                }

                &::before {
                    background: var(--primary);
                    height: 50px;
                }
            }

            &.previous {
                > .top {
                    width: 200px;
                }
            }
        }
    }

    .button.next,
    .button.previous {
        background-color: var(--primary);
        border-radius: 50px;
        padding: 6px !important;
        height: 50px !important;
        width: 50px !important;

        > i {
            font-size: 30px !important;
        }
    }

    .button.next {
        right: 20px;
        z-index: 5;
    }

    .button.previous {
        left: 20px;
    }
}
</style>

